import React, { useEffect, useState, useRef } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Button, Card, Divider } from '@sunwisesoftware/sunwise-ui';
import { debounce, isEmpty, isEqual } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { CustomPagination } from 'common/components';
import { DEBOUNCE_TIME_500, TABLE_PAGE_SIZE_OPTIONS } from 'common/constants';
import CustomColumns from 'common/modules/customColumns';
import { getOrderedColumns, getHiddenColumns } from 'common/utils/helpers';

import { COLUMNS_CONFIG } from '../constants';

import BulkOperationsCard from './BulkOperationsCard';
import ItemsFilter from './ItemsFilter';
import ProductColumns from './ProductColumns';

const ListItems = ({
    filterData,
    filterItems,
    handleClickAdd,
    handleClickBulkItems,
    handleClickUpdate,
    isFetching,
    items,
    paginationData,
    setFilterData,
    tableSettings,
}) => {
    const { t } = useTranslation();

    const [currentFilterData, setCurrentFilterData] = useState(filterData);
    const [rowCount, setRowCount] = useState(paginationData?.rowCount || 0);
    const [rowModesModel, setRowModesModel] = useState({});
    const [selectedBulkOption, setSelectedBulkOption] = useState('');
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [selectionModel, setSelectionModel] = useState([]);

    const handleOnFilter = useRef(
        debounce((filter) => filterItems(filter), DEBOUNCE_TIME_500),
    ).current;

    const handleOnChangeSearch = (event) =>
        setFilterData((prev) => ({
            ...prev,
            page: 0,
            searchText: event.target.value,
        }));

    const handleOnSelectStatus = (value) =>
        setFilterData((prev) => ({ ...prev, page: 0, status: value }));

    const pageSizeOptions = TABLE_PAGE_SIZE_OPTIONS;

    const columns = ProductColumns({
        filterData,
        handleClickBulkItems,
        handleClickUpdate,
        hiddenColumns: getHiddenColumns(tableSettings),
        orders: getOrderedColumns(tableSettings),
        rowModesModel,
        selectedRowId,
        setRowModesModel,
        setSelectedRowId,
    });
    const getRowHeight = () => 'auto';
    const columnVisibilityModel = columns.reduce(
        (acc, column) => ({ ...acc, [column.field]: !column.hide }),
        {},
    );
    const initialState = {
        pagination: { paginationModel: { pageSize: pageSizeOptions[0] } },
    };
    const onPaginationModelChange = (model) =>
        setFilterData((prev) => ({ ...prev, ...model }));
    const onRowSelectionModelChange = (model) => setSelectionModel(model);
    const onSortModelChange = (model) => {
        if (!model[0]) return;
        setFilterData((prev) => ({
            ...prev,
            orderBy: model[0].field.toLocaleLowerCase(),
            sortBy: model[0].sort,
        }));
    };
    const paginationModel = {
        page: paginationData?.current_page - 1 || 0,
        pageSize: filterData?.pageSize || pageSizeOptions[0],
    };

    const slotProps = {
        pagination: {
            customContent: (
                <CustomColumns.Container
                    columnsConfig={COLUMNS_CONFIG}
                    tableKey="panels"
                />
            ),
            pageSizeOptions,
        },
    };
    const slots = { pagination: CustomPagination };

    useEffect(() => {
        if (isEmpty(paginationData)) return;
        setRowCount((prev) =>
            paginationData?.total_rows !== undefined
                ? paginationData?.total_rows
                : prev,
        );
    }, [paginationData?.total_rows]);

    useEffect(() => {
        if (isEqual(currentFilterData, filterData)) return;
        handleOnFilter(filterData);
        setCurrentFilterData(filterData);
    }, [filterData]);

    return (
        <>
            <ItemsFilter
                filterData={filterData}
                handleOnChangeSearch={handleOnChangeSearch}
                handleOnSelectStatus={handleOnSelectStatus}
            />

            <Card>
                <Card.Body>
                    <Box sx={{ textAlign: 'right' }}>
                        <Button endIcon={<AddIcon />} onClick={handleClickAdd}>
                            {t('Add')}
                        </Button>

                        <Divider sx={{ my: 2 }} />
                    </Box>

                    <BulkOperationsCard
                        filterData={filterData}
                        handleClickBulkItems={handleClickBulkItems}
                        handleOnChangeSearch={handleOnChangeSearch}
                        handleOnSelectStatus={handleOnSelectStatus}
                        selectedBulkOption={selectedBulkOption}
                        selectionModel={selectionModel}
                        setSelectedBulkOption={setSelectedBulkOption}
                        setSelectionModel={setSelectionModel}
                    />

                    <DataGrid
                        checkboxSelection={true}
                        columns={columns}
                        columnVisibilityModel={columnVisibilityModel}
                        disableColumnMenu
                        disableRowSelectionOnClick
                        getRowHeight={getRowHeight}
                        hideFooterSelectedRowCount
                        initialState={initialState}
                        loading={isFetching}
                        onPaginationModelChange={onPaginationModelChange}
                        onRowSelectionModelChange={onRowSelectionModelChange}
                        onSortModelChange={onSortModelChange}
                        pageSizeOptions={pageSizeOptions}
                        pagination
                        paginationMode="server"
                        paginationModel={paginationModel}
                        rowCount={rowCount}
                        rowSelectionModel={selectionModel}
                        rows={items}
                        slotProps={slotProps}
                        slots={slots}
                        sortingMode="server"
                    />
                </Card.Body>
            </Card>
        </>
    );
};

ListItems.propTypes = {
    filterData: PropTypes.object,
    filterItems: PropTypes.func,
    handleClickAdd: PropTypes.func,
    handleClickBulkItems: PropTypes.func,
    handleClickUpdate: PropTypes.func,
    isFetching: PropTypes.bool,
    items: PropTypes.array,
    paginationData: PropTypes.object,
    setFilterData: PropTypes.func,
    tableSettings: PropTypes.array,
};

export default ListItems;
